import { InjectionToken } from '@angular/core';

/**
 * Request the localStorage object. If it's unavailable, accept a fallback to sessionStorage instead.
 */
export const LOCAL_STORAGE = new InjectionToken<Storage>('LocalStorage');

/**
 * Request the localStorage object. No fallback allowed: if localStorage is absent or has zero quota, this will provide null.
 */
export const LOCAL_STORAGE_STRICT = new InjectionToken<Storage>('LocalStorage (Strict)');

/**
 * Request the sessionStorage object. If it's unavailable, accept a fallback to localStorage instead.
 */
export const SESSION_STORAGE = new InjectionToken<Storage>('SessionStorage');

/**
 * Request the sessionStorage object. No fallback allowed: if sessionStorage is absent or has zero quota, this will provide null.
 */
export const SESSION_STORAGE_STRICT = new InjectionToken<Storage>('SessionStorage (Strict)');

const storageAvailable = (storage: Storage): boolean => {
  if (!storage) {
    return false;
  }

  try {
    const test = '__storage_test__';
    storage.setItem(test, test);
    storage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get a storage object safely.
 *
 * @param storage The storage object to get
 * @returns The storage object if it exists and has quota; null otheriwse.
 */
 export const getStorage = (storage: Storage) => {
  if (storageAvailable(storage)) {
    return storage;
  }
  return undefined;
};

export const localStorageFactory = (window: Window) => {
  return getStorage(window.localStorage) ?? getStorage(window.sessionStorage) ?? undefined;
};

export const localStorageStrictFactory = (window: Window) => {
  return getStorage(window.localStorage) ?? undefined;
};

export const sessionStorageFactory = (window: Window) => {
  return getStorage(window.sessionStorage) ?? getStorage(window.localStorage) ?? undefined;
};

export const sessionStorageStrictFactory = (window: Window) => {
  return getStorage(window.sessionStorage) ?? undefined;
};
